import { useApiWithAuth } from '@/hooks/api';
import { useUsersStore } from '@/store/users';
import {
  archiveUser,
  createUser,
  loadUsers,
  resendInvite,
  updateUser,
} from '@/store/users/actions';
import type { UserStatus } from '@/store/users/types';
import type { User } from '@/shared/types/user';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Spacer,
  Tooltip,
  useDisclosure,
} from '@heroui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type EditMode,
  UserEdit,
  type UserSavePayload,
  type UserSource,
} from './modal';
import type { ActionKey } from './columns';
import { getItemsMatchingSearch, makeUser } from './utils';
import { StatusSelect } from './status';
import { UserSearch } from './search';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useUserStatusList } from './hooks';
import { UsersTable } from './table';
import { LoadingBar } from '@/components/common/loading';

export default function Users() {
  const initial = useRef(false);
  const store = useUsersStore();
  const { api, auth } = useApiWithAuth();
  const doLoad = useCallback(
    async (force: boolean) => {
      setLoading(true);
      if (await loadUsers(store, { force, auth, api })) {
        initial.current = true;
      }

      setLoading(false);
    },
    [api, auth, store],
  );
  useEffect(() => {
    if (initial.current) {
      return;
    }

    doLoad(false);
  }, [store.state.loaded, store.dispatch, auth, api, doLoad]);

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<UserStatus>('active');
  const allItems = useUserStatusList(status);
  const [search, setSearch] = useState('');
  const items = useMemo(
    () => getItemsMatchingSearch(allItems, search),
    [allItems, search],
  );

  const userEdit = useDisclosure();
  const [editing, setEditing] = useState<{
    mode: EditMode;
    source: UserSource;
    item: User | null;
  }>({ mode: 'create', source: 'new-user', item: null });
  const userEditOnClose = () => {
    setEditing({ item: null, mode: 'create', source: 'new-user' });
  };

  const onAction = (item: User, action: ActionKey) => {
    if (action === 'edit') {
      setEditing({ item, mode: 'update', source: 'new-user' });
      userEdit.onOpenChange();
    }

    if (action === 'invite') {
      resendInvite({ api, auth, item });
    }

    if (action === 'archive') {
      archiveUser(store, { api, auth, item });
    }
  };

  const onCreate = (source: UserSource) => {
    setEditing({ item: makeUser(), mode: 'create', source });
    userEdit.onOpenChange();
  };

  const onReload = () => {
    doLoad(true);
  };

  const [saving, setSaving] = useState(false);
  const onSave = async (payload: UserSavePayload) => {
    setSaving(true);
    const action =
      payload.mode === 'create'
        ? createUser(store, {
            api,
            auth,
            item: payload.user,
            invite: payload.invite,
          })
        : updateUser(store, { api, auth, item: payload.user });
    await action;
    try {
      userEdit.onClose();
      setSaving(false);
      setEditing({ item: null, mode: 'create', source: 'new-user' });
    } catch (e) {
      console.error(e);
      setSaving(false);
    }
  };

  return (
    <div>
      <div className='mb-3 flex flex-row items-center space-x-2'>
        <UserSearch onChange={setSearch} className='basis-1/2' />
        <StatusSelect
          status={status}
          onChange={setStatus}
          className='basis-1/4'
        />
        <Spacer className='grow' />
        <Tooltip content='Reload Users'>
          <Button
            variant='light'
            isIconOnly
            onPress={onReload}
            className='text-default-700'
          >
            <Icon icon='mdi:reload' width={20} />
          </Button>
        </Tooltip>
        <Dropdown>
          <DropdownTrigger>
            <Button variant='solid' color='primary'>
              Create
              <Icon icon='mdi:plus' width={20} />
            </Button>
          </DropdownTrigger>
          <DropdownMenu>
            <DropdownItem key='new-user' onPress={() => onCreate('new-user')}>
              New User
            </DropdownItem>
            <DropdownItem
              key='from-profile'
              onPress={() => onCreate('from-profile')}
            >
              From Profile
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>
      <LoadingBar loading={loading} />
      <UsersTable items={items} count={allItems.length} onAction={onAction} />
      <UserEdit
        isOpen={userEdit.isOpen}
        onOpenChange={userEdit.onOpenChange}
        onClose={userEditOnClose}
        item={editing.item}
        mode={editing.mode}
        source={editing.source}
        onSave={onSave}
        saving={saving}
      />
    </div>
  );
}
